/* eslint-disable global-require */
import { memoize } from 'lodash/fp';
import { PRODUCTION } from '../../components/app/index.constants';

let createSnowplowTracker;
let getSnowplowTracker;
let HudlDataSchemas;
try {
  // Defensive require: handle missing or incomplete exports for Storybook
  const analytics = require('@hudl/analytics') || {};
  createSnowplowTracker = analytics.createSnowplowTracker;
  getSnowplowTracker = analytics.getSnowplowTracker;
  HudlDataSchemas = analytics.HudlDataSchemas;
} catch (err) {
  // eslint-disable-next-line no-console
  console.warn('[snowplow] Could not load @hudl/analytics:', err);
}

// only call if available
export const initialiseSnowplow = memoize(async (userId, entityName) => {
  if (!createSnowplowTracker || !HudlDataSchemas || !getSnowplowTracker) return;

  createSnowplowTracker({
    id: 'hudlIQ',
    platform: 'web',
    env: process.env.NODE_ENV === PRODUCTION ? 'prod' : 'thor',
  });

  const tracker = getSnowplowTracker('hudlIQ');

  const applicationEntity =
    HudlDataSchemas.ApplicationEntity?.V1_0_0?.describe?.({
      app_name: 'HudlIQWeb',
    });
  const userOrgEntity = HudlDataSchemas.UserOrgEntity?.V1_0_0?.describe?.({
    org_id: entityName,
    org_name: entityName,
  });
  const userEntity = HudlDataSchemas.UserEntity?.V4_0_3?.describe?.({
    user_id: userId,
  });

  if (applicationEntity && userOrgEntity && userEntity) {
    tracker.addGlobalContexts('hudlIQ', [
      applicationEntity,
      userOrgEntity,
      userEntity,
    ]);
  }
});

// Snowplow events
const trackEvent = (payload, context = []) => {
  try {
    if (getSnowplowTracker) {
      getSnowplowTracker('hudlIQ').trackSelfDescribingEvent(payload, context);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[snowplow] init error', err);
  }
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/content_favorite_event/jsonschema/2-0-0
export const trackContentFavoriteEvent = (
  actionType,
  contentName,
  favouritedLocation,
  context = [],
) => {
  if (!HudlDataSchemas?.ContentFavoriteEvent?.V2_0_0?.describe) return;
  trackEvent(
    HudlDataSchemas.ContentFavoriteEvent.V2_0_0.describe({
      action_type: actionType,
      favorited_location: favouritedLocation,
      content_name: contentName,
    }),
    context,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_change_event/jsonschema/1-0-14
export const trackObjectChangeEvent = (
  objectType,
  changeType,
  info,
  context = [],
) => {
  if (!HudlDataSchemas?.ObjectChangeEvent?.V1_0_14?.describe) return;
  trackEvent(
    HudlDataSchemas.ObjectChangeEvent.V1_0_14.describe({
      object_type: objectType, // Widget
      change_type: changeType, // Edit, Delete, Add, Rename, Duplicate
      context: info,
    }),
    context,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/content_search_event/jsonschema/2-0-3
export const trackContentSearchEvent = (searchTerm, source, context = []) => {
  if (!HudlDataSchemas?.ContentSearchEvent?.V2_0_3?.describe) return;
  trackEvent(
    HudlDataSchemas.ContentSearchEvent.V2_0_3.describe({
      keyword: searchTerm,
      source,
      // result_section_name,
      // number_of_results,
    }),
    context,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_open_close_event/jsonschema/1-0-10
export const trackObjectOpenCloseEvent = (
  type,
  isOpen,
  description,
  context = [],
) => {
  if (!HudlDataSchemas?.ObjectOpenCloseEvent?.V1_0_10?.describe) return;
  trackEvent(
    HudlDataSchemas.ObjectOpenCloseEvent.V1_0_10.describe({
      object_type: type,
      open_vs_close: isOpen ? 'open' : 'close',
      context: description,
    }),
    context,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_creation_event/jsonschema/1-0-10
export const trackObjectCreationEvent = (type, description, context = []) => {
  if (!HudlDataSchemas?.ObjectCreationEvent?.V1_0_10?.describe) return;
  trackEvent(
    HudlDataSchemas.ObjectCreationEvent.V1_0_10.describe({
      object_type: type, // DashboardList, Widget
      context: description,
    }),
    context,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/object_save_event/jsonschema/1-0-1
export const trackObjectSaveEvent = (type, saveType, context = []) => {
  if (!HudlDataSchemas?.ObjectSaveEvent?.V1_0_1?.describe) return;
  trackEvent(
    HudlDataSchemas.ObjectSaveEvent.V1_0_1.describe({
      object_type: type, // Organizer
      save_type: saveType, // Save, Save As
    }),
    context,
  );
};

// https://github.com/hudl/data-schemas/blob/master/schemas/com.hudl/content_playback_event/jsonschema/3-0-5
export const trackVideoViewEvent = (videoId, context = []) => {
  if (!HudlDataSchemas?.ContentPlaybackEvent?.V3_0_5?.describe) return;
  trackEvent(
    HudlDataSchemas.ContentPlaybackEvent.V3_0_5.describe({
      context: videoId,
      content_type: 'Video',
      playback_action_type: 'Play',
    }),
    context,
  );
};
